/*
 * What the page in front of the attorney shows, for the global Ask Casey
 * panel: the record a page has open (a template in its editor, a company, a
 * library entry, a time entry), with its selection and unsaved state when the
 * page knows them. Pages publish it (`useOnScreen`); a page that publishes
 * nothing still names its record in the URL, read with `parseEntityHref`.
 * The server checks whatever is sent against the firm before it trusts it.
 *
 * Pure: no React or IO here.
 */
import {
  parseEntityHref,
  type EntityKind,
  type EntityRefTarget,
  type RecordFocusKey
} from "../refs/entityRefs";

export type OnScreen = {
  kind: EntityKind;
  id: string;
  /** What the page calls it; null when only the URL named it. */
  label: string | null;
  client_id?: string;
  company_id?: string;
  /** For a client `record`: which history. */
  focus_key?: RecordFocusKey;
  selected_node_ids?: string[];
  selected_text?: string;
  /** The page holds edits it has not saved yet. */
  dirty?: boolean;
  /** The day the page shows (the timesheet). */
  date?: string;
};

/** A recently viewed record, as sent: no selection, nothing live. */
export type RecentOnScreen = Pick<
  OnScreen,
  "kind" | "id" | "label" | "client_id" | "company_id" | "focus_key"
>;

export const RECENT_LIMIT = 5;

/** The kinds a page can have open (a client or case is a focus, not a record). */
const OPEN_KINDS: ReadonlySet<EntityKind> = new Set<EntityKind>([
  "template",
  "company",
  "rule",
  "library",
  "document",
  "reminder",
  "record",
  "intake",
  "time_entry"
]);

export const onScreenKey = (e: Pick<OnScreen, "kind" | "id">): string =>
  `${e.kind}:${e.id}`;

/** A URL's record as an unlabelled on-screen entry, or null. */
export const onScreenFromTarget = (
  target: EntityRefTarget | null
): OnScreen | null => {
  if (!target?.id || !OPEN_KINDS.has(target.kind)) return null;
  return {
    kind: target.kind,
    id: target.id,
    label: null,
    ...(target.clientId ? { client_id: target.clientId } : {}),
    ...(target.companyId ? { company_id: target.companyId } : {}),
    ...(target.focusKey ? { focus_key: target.focusKey } : {})
  };
};

export const onScreenFromRoute = (route: string): OnScreen | null =>
  onScreenFromTarget(parseEntityHref(route));

/**
 * The record the panel sends as open: what the page published, else what its
 * URL names, unless the attorney removed it from the composer.
 */
export const openOnScreen = (
  published: OnScreen | null,
  route: string,
  dismissedKey: string | null
): OnScreen | null => {
  const open = published ?? onScreenFromRoute(route);
  if (!open || onScreenKey(open) === dismissedKey) return null;
  return open;
};

export const toRecent = (e: OnScreen): RecentOnScreen => ({
  kind: e.kind,
  id: e.id,
  label: e.label,
  ...(e.client_id ? { client_id: e.client_id } : {}),
  ...(e.company_id ? { company_id: e.company_id } : {}),
  ...(e.focus_key ? { focus_key: e.focus_key } : {})
});

/** `recent` with `entry` moved to the front, one per key, at most the limit. */
export const pushRecent = (
  recent: readonly RecentOnScreen[],
  entry: OnScreen
): RecentOnScreen[] => {
  const key = onScreenKey(entry);
  const kept = recent.filter((r) => onScreenKey(r) !== key);
  return [toRecent(entry), ...kept].slice(0, RECENT_LIMIT);
};
